import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';

import { NS } from '@/constants';

import { useAppDispatch, useAppSelector } from '@/redux';
import { selectIsUserConnected } from '@/redux/slices/auth.slice';
import { selectCartContent, selectRelevantCartDetailsForCheckout } from '@/redux/slices/cart.slice';
import {
  selectCurrentStepLabel, selectIsAddressOk,
  selectLastOrder,
  selectPaymentProviderInfos,
  selectShippingList
} from '@/redux/slices/checkout.slice';
import { selectUserAddresses } from '@/redux/slices/user.slice';
import { selectStoreId } from '@/redux/slices/root.slice';

import { useSidebarContinueButton } from '@/components/checkout/section/sidebar/presenters/useSidebarContinueButton';
import { useCollapsableDetails } from '@/components/checkout/section/sidebar/presenters/useCollapsableDetails';
import { useSidebarPricesSection } from '@/components/checkout/section/sidebar/presenters/useSidebarPricesSection';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';
import { AddressModel } from '@/models';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { selectIsAdminActAsCustomer } from '@/redux/slices/admin.slice';
import { PREMIUM_S3_URL } from '@/constants/urls';
import { STORE_ID } from '@/constants/stores';

const NAMESPACES = [
  NS.COMMON,
  NS.CHECKOUT,
  NS.CUSTOMIZATION,
  NS.ADMIN,
  NS.CELLAR,
  NS.ALTER
];

export const useSidebarPresenter = () => {
  function getToggleShippingAsBilling() {
    if (currentStep !== 'address') {
      return { shouldShow: false };
    }
    if (userAddresses?.length === 0) {
      return { shouldShow: true, shouldDisable: true };
    }
    const address = userAddresses?.find((address) => address.idNext === cartContent.shipping?.idNext) || {} as AddressModel;
    const toggleShippingAsBilling = (use: boolean) => {
      dispatch(updateCartAttributeByName({ attributeName: 'billing', newValue: use ? address : {} }));
    };

    return {
      shouldShow: true,
      shouldDisable: false,
      isSameAddress: cartContent.shipping?.idNext === cartContent.billing?.idNext,
      onCheckOrUncheck: toggleShippingAsBilling
    };
  }

  function confirmLegalConditions() {
    return {
      shouldShow: currentStep === 'pay',
      state: doesUserHasAcceptedCgv,
      change: setAcceptCgv
    };
  }

  function legalConditions() {
    return (
      {
        cgu: {
          shouldShow: isUsa,
          label: t('legalInformations.prompt'),
          linkLabel: t('legalInformations.showCgu'),
          href: `${PREMIUM_S3_URL}/cgu-usa.pdf`
        },
        cgv: {
          shouldShow: true,
          label: t('legalInformations.prompt'),
          linkLabel: t('legalInformations.showCgv'),
          href: '/cgv'
        }
      }
    );
  }

  const { t } = useTranslation(NAMESPACES);
  const dispatch = useAppDispatch();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isUserConnected = useAppSelector(selectIsUserConnected);
  const isMobile = useScreenSizeContext().isMobile;
  const isDesktop = useScreenSizeContext().isDesktop;
  const currentStep = useAppSelector(selectCurrentStepLabel);
  const paymentProvider = useAppSelector(selectPaymentProviderInfos);
  const {
    billingAddress,
    shippingMode,
    paymentMode,
    prearrivalShippingMode,
    primeurShippingMode
  } = useAppSelector(selectRelevantCartDetailsForCheckout);
  const isUsa = useAppSelector(selectStoreId) === STORE_ID.USA;
  const cartContent = useAppSelector(selectCartContent);
  const lastOrder = useAppSelector(selectLastOrder);
  const userAddresses = useAppSelector(selectUserAddresses);
  const shippingMap = useAppSelector(selectShippingList);
  const isAddressOk = useAppSelector(selectIsAddressOk);

  const finalContent = currentStep === 'report' ? lastOrder : cartContent;

  const [doesUserHasAcceptedCgv, setAcceptCgv] = useState(false);
  const continueButton = useSidebarContinueButton({
    t, dispatch, buttonRef, isUserConnected, isMobile, currentStep, userHasAcceptedCgv: doesUserHasAcceptedCgv,
    paymentProvider, billingAddress, shippingMode, paymentMode,
    prearrivalShippingMode, primeurShippingMode, isUsa, shippingMap, isAddressOk
  });
  const collapsableDetails = useCollapsableDetails({ isUsa, t, cartContent: finalContent });
  const pricesSection = useSidebarPricesSection(currentStep === 'report', true);
  const actAsCustomer = useAppSelector(selectIsAdminActAsCustomer);

  const priceOverriding = {
    shouldShow: actAsCustomer && currentStep !== 'report'
  };

  const discountForm = {
    shouldShow: !['pay', 'report'].includes(currentStep) || (currentStep === 'report' && finalContent.discountCode?.name),
    codeName: finalContent.discountCode?.name || '',
    isReadonly: currentStep === 'report'
  };

  const applePayButton = {
    shouldShow:
      !['address', 'billing', 'shipping', 'review', 'pay', 'report'].includes(currentStep) &&
      isUserConnected
  };

  const clientIsEligibleForTaxFreeBilling = {
    shouldShow: finalContent.isTaxFreeBilling,
    info: t('checkout:taxFreeBillingInformation'),
    label: t('checkout:withoutTax')
  };

  const promotionIncentive = {
    shouldShow: currentStep === 'cart' && cartContent.promotionsNotCompleted?.length > 0,
    promos: cartContent.promotionsNotCompleted
  };

  return {
    continueButton, collapsableDetails, pricesSection, toggleShippingAsBilling: getToggleShippingAsBilling(),
    translate: t, discountForm, isMobile, isDesktop, applePayButton, legalConditions: legalConditions(),
    confirmLegalConditions: confirmLegalConditions(), userHasAcceptedCgv: doesUserHasAcceptedCgv,
    clientIsEligibleForTaxFreeBilling, promotionIncentive, priceOverriding
  };
};
